import { logError } from "@/shared/logging/errors";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { appEvents } from "@/app/events/appEvents";
import { globalStores } from "@/app/globalStores";
import { trans } from "@/app/localization/Trans";
import { Popup } from "@/app/ui/Popup";
import { logInformation } from "@/shared/logging/informations";

// on a terminal that is a touch, a key of the on screen keyboard or a scrolled list
const activityEvents = ["pointerdown", "keydown", "scroll"];

export interface IKioskInactivityMonitorOptions {
    isOrderFlowVisible(): boolean;
    isStartPageVisible(): boolean;
}

/**
 * Asks the customer whether the order should go on after a while without input, and hands the
 * terminal to the next customer both when they start over and when the popup is left unanswered.
 * On the start page there is no order to ask about, so it goes to the screen saver right away.
 */
export class KioskInactivityMonitor {
    private readonly options: IKioskInactivityMonitorOptions;
    private readonly activityHandler: () => void;

    private inactivityTimer?: number;
    private popupTimer?: number;
    private $popup?: JQuery | null;

    constructor(options: IKioskInactivityMonitorOptions) {
        this.options = options;
        this.activityHandler = () => this.onActivity();
    }

    public enable() {
        for (const eventName of activityEvents) {
            document.addEventListener(eventName, this.activityHandler, { capture: true, passive: true });
        }

        this.restartInactivityTimer();
        logInformation("KioskInactivityMonitor.enable - enabled");
    }

    private onActivity() {
        // once the popup is up only its buttons count, its 30s run on to the screen saver
        if (this.$popup != null) return;

        this.restartInactivityTimer();
    }

    private restartInactivityTimer() {
        window.clearTimeout(this.inactivityTimer);
        this.inactivityTimer = window.setTimeout(async () => this.onInactive(), config.kioskInactivityMs);
    }

    private async onInactive() {
        // nobody is at the terminal, keep watching for when somebody is
        if (!this.isTerminalUncovered()) {
            this.restartInactivityTimer();
            return;
        }

        // the start page has nothing to continue, so it needs no question - hand the terminal
        // to the next customer straight away
        if (this.options.isStartPageVisible()) {
            await this.resetForNextCustomer();
            return;
        }

        // there is nothing to continue on this view either, keep watching for when there is
        if (!this.options.isOrderFlowVisible()) {
            this.restartInactivityTimer();
            return;
        }

        this.showPopup();
    }

    private isTerminalUncovered() {
        return globalStores.kioskStore?.isKioskMode === true && !globalStores.kioskStore.isScreenSaverVisible;
    }

    private showPopup() {
        // whatever the customer left open on top of the order has to give way to the question
        appEvents.kiosk.inactivityPopupShown.dispatch();

        this.$popup = Popup.showWithOptions({
            message: trans.continueOrderPopover.title,
            uniqueId: "kioskinactivity",
            // the flag adds the cancel button first, which puts start over on the left
            confirmBtnOnTheLeft: true,
            cancelButton: trans.continueOrderPopover.startOver,
            highlightCancel: true,
            cancelHandler: () => this.onStartOver(),
            confirmButton: trans.continueOrderPopover.continueOrder,
            confirmHandler: () => this.onContinue(),
        });

        this.popupTimer = window.setTimeout(() => this.onPopupUnanswered(), config.kioskInactivityPopupMs);
    }

    // Popup closes itself before the confirm handler but after the cancel one, so neither may
    // rely on the popup still being there
    private onContinue() {
        this.forgetPopup();
        this.restartInactivityTimer();
    }

    private async onStartOver() {
        this.forgetPopup();

        // the cart belongs to the identity the reset replaces, so it has to go first. the reset
        // also hides any popover and resets the order view through the reallocation it triggers,
        // so nothing has to be torn down here
        await this.removeCart();

        // no screen saver here, unlike the unanswered case: this guest is still standing at the
        // terminal and asked for a new order, so the reset goes straight to the start page it
        // navigates to. the language is still reset, they start from scratch. that leaves the
        // terminal live, so it has to go back under watch - nothing else re-arms it until the
        // next touch
        await globalStores.kioskStore?.resetSession(false, true);
        this.restartInactivityTimer();
    }

    private async onPopupUnanswered() {
        const $popup = this.$popup;
        this.forgetPopup();
        if ($popup != null) Popup.close($popup);

        await this.resetForNextCustomer();
    }

    // nothing re-arms the timer until the next touch on the screen saver, which is what the
    // activity handler is for
    private async resetForNextCustomer() {
        // the screen saver goes up before the two requests below, not with the reset behind
        // them: the customer has left, and the terminal must not stand there showing their
        // order for the second or two the requests take
        globalStores.kioskStore?.showScreenSaver();

        // the cart belongs to the identity the reset replaces, so it has to go first
        await this.removeCart();
        await globalStores.kioskStore?.resetSession();
    }

    private async removeCart() {
        try {
            await TastierClient.context.removeCartsFromLocation();
        } catch (ex) {
            logError(ex);
        }
    }

    private forgetPopup() {
        window.clearTimeout(this.popupTimer);
        this.popupTimer = undefined;
        this.$popup = undefined;
    }
}
